import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Plus, RefreshCw, Search } from "lucide-react";
import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Can } from "@/features/auth/components/Can";
import { ACTIONS } from "@/constants/roles";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { clientService } from "@/features/clients/services/client.service";
import { clientAdminService } from "@/features/clients/services/client-admin.service";
import type { ClientResponseDTO } from "@/features/clients/types/client.types";
import { GenderType, PersonType } from "@/shared/types/enums";
import { getApiErrorMessage } from "@/shared/lib/api-error";
import { applyFieldErrorsToForm } from "@/shared/lib/form-error";
import { toast } from "sonner";
import { ClientsTable } from "@/features/clients/components/ClientsTable";
import { ClientFormDialog } from "@/features/clients/components/ClientFormDialog";
import { ClientDeleteDialog } from "@/features/clients/components/ClientDeleteDialog";
import { ClientDetailsModal } from "@/features/clients/components/ClientDetailsModal";
import { CNPJ_LENGTH, CPF_LENGTH, cleanDocument } from "@/shared/lib/document";

const DIALOG_MODE = {
  VIEW: "view",
  CREATE: "create",
  EDIT: "edit",
  DELETE: "delete",
} as const;

type DialogMode = (typeof DIALOG_MODE)[keyof typeof DIALOG_MODE];

const clientSchema = z
  .object({
    personType: z.nativeEnum(PersonType),
    name: z.string().min(2, "Nome deve ter pelo menos 2 caracteres").max(150),
    document: z.string().min(1, "Documento é obrigatório").max(20),
    gender: z
      .enum([GenderType.FEMALE, GenderType.MALE])
      .or(z.literal(""))
      .optional(),
    phone: z.string().min(1, "Telefone é obrigatório").max(20),
    email: z.string().email("Email inválido").optional().or(z.literal("")),
    street: z.string().min(1, "Rua é obrigatória").max(255),
    number: z
      .string()
      .min(1, "Número é obrigatório")
      .max(10, "Número deve ter no máximo 10 caracteres"),
    complement: z.string().min(1, "Complemento é obrigatório").max(255),
    neighborhood: z.string().min(1, "Bairro é obrigatório").max(255),
    state: z
      .string()
      .min(2, "Estado deve ter 2 caracteres")
      .max(2, "Estado deve ter 2 caracteres"),
    postalCode: z.string().min(1, "CEP é obrigatório").max(20),
  })
  .superRefine((data, ctx) => {
    const document = cleanDocument(data.document);
    if (data.personType === PersonType.FISICA) {
      if (document.length !== CPF_LENGTH) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["document"],
          message: "CPF deve ter 11 dígitos",
        });
      }
      if (!data.gender) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["gender"],
          message: "Gênero é obrigatório para pessoa física",
        });
      }
    } else {
      if (document.length !== CNPJ_LENGTH) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["document"],
          message: "CNPJ deve ter 14 caracteres",
        });
      }
    }
  });

type ClientForm = z.infer<typeof clientSchema>;

export function ClientesPage() {
  const { isAdmin } = useAuth();

  const [clients, setClients] = useState<ClientResponseDTO[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  // Filtros avançados (apenas para admin)
  const [filterName, setFilterName] = useState("");
  const [filterDocument, setFilterDocument] = useState("");
  const [filterGender, setFilterGender] = useState<string>("");
  const [filterPhone, setFilterPhone] = useState("");
  const [filterEmail, setFilterEmail] = useState("");

  const [dialogMode, setDialogMode] = useState<DialogMode | null>(null);
  const [dialogContent, setDialogContent] = useState<DialogMode | null>(null);
  const [selectedClient, setSelectedClient] =
    useState<ClientResponseDTO | null>(null);

  const form = useForm<ClientForm>({
    resolver: zodResolver(clientSchema),
    defaultValues: {
      personType: PersonType.FISICA,
      name: "",
      document: "",
      gender: GenderType.MALE,
      phone: "",
      email: "",
      street: "",
      number: "",
      complement: "",
      neighborhood: "",
      state: "DF",
      postalCode: "",
    },
  });

  const loadControllerRef = useRef<AbortController | null>(null);

  const loadClients = useCallback(
    async (page: number = 0) => {
      loadControllerRef.current?.abort();
      const controller = new AbortController();
      loadControllerRef.current = controller;
      setLoading(true);
      try {
        let response;

        if (isAdmin) {
          response = await clientAdminService.search(
            {
              name: filterName || undefined,
              document: filterDocument || undefined,
              gender: (filterGender as GenderType) || undefined,
              phone: filterPhone || undefined,
              email: filterEmail || undefined,
              page,
              size: pageSize,
              sortBy: "name",
              sortDirection: "ASC",
            },
            { signal: controller.signal },
          );
        } else {
          response = await clientService.list(
            { page, size: pageSize },
            { signal: controller.signal },
          );
        }

        if (controller.signal.aborted) return;
        setClients(response.content);
        setTotalPages(response.totalPages);
        setTotalElements(response.totalElements);
        setCurrentPage(page);
      } catch (error: unknown) {
        if (controller.signal.aborted) return;
        toast.error(getApiErrorMessage(error, "Erro ao carregar clientes"));
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    },
    [
      pageSize,
      isAdmin,
      filterName,
      filterDocument,
      filterGender,
      filterPhone,
      filterEmail,
    ],
  );

  useEffect(() => () => loadControllerRef.current?.abort(), []);

  useEffect(() => {
    // Filtros disparam via botão Buscar — loadClients os captura via closure.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    loadClients(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageSize]);

  const filteredClients = useMemo(() => {
    if (!searchTerm) return clients;
    const search = searchTerm.toLowerCase();
    return clients.filter(
      (client) =>
        client.name.toLowerCase().includes(search) ||
        client.document.toLowerCase().includes(search) ||
        client.phone.toLowerCase().includes(search) ||
        client.email?.toLowerCase().includes(search),
    );
  }, [clients, searchTerm]);

  const handleSearch = useCallback(() => {
    loadClients(0);
  }, [loadClients]);

  // Timeout da animação de fechamento — cancelado ao reabrir para o timer
  // antigo não zerar o estado (conteúdo/cliente/form) do dialog novo.
  const dialogCloseTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (dialogCloseTimeout.current) clearTimeout(dialogCloseTimeout.current);
    },
    [],
  );

  const handleOpenDialog = useCallback(
    (mode: DialogMode, client?: ClientResponseDTO) => {
      if (dialogCloseTimeout.current) {
        clearTimeout(dialogCloseTimeout.current);
        dialogCloseTimeout.current = null;
      }
      setDialogMode(mode);
      setDialogContent(mode);
      setSelectedClient(client || null);

      if (mode === DIALOG_MODE.EDIT && client) {
        form.reset({
          personType: client.personType,
          name: client.name,
          document: client.document,
          gender: client.gender ?? "",
          phone: client.phone,
          email: client.email || "",
          street: client.street,
          number: client.number,
          complement: client.complement,
          neighborhood: client.neighborhood,
          state: client.state,
          postalCode: client.postalCode,
        });
      } else if (mode === DIALOG_MODE.CREATE) {
        form.reset({
          personType: PersonType.FISICA,
          name: "",
          document: "",
          gender: GenderType.MALE,
          phone: "",
          email: "",
          street: "",
          number: "",
          complement: "",
          neighborhood: "",
          state: "DF",
          postalCode: "",
        });
      }
    },
    [form],
  );

  const handleCloseDialog = useCallback(() => {
    setDialogMode(null);
    dialogCloseTimeout.current = setTimeout(() => {
      setDialogContent(null);
      setSelectedClient(null);
      form.reset();
      dialogCloseTimeout.current = null;
    }, 300);
  }, [form]);

  const onSubmit = useCallback(
    async (data: ClientForm) => {
      setLoading(true);
      try {
        const isJuridica = data.personType === PersonType.JURIDICA;
        const payload = {
          name: data.name,
          document: data.document,
          personType: data.personType,
          gender: isJuridica ? null : (data.gender as GenderType),
          phone: data.phone,
          email: data.email || undefined,
          street: data.street,
          number: data.number,
          complement: data.complement,
          neighborhood: data.neighborhood,
          state: data.state,
          postalCode: data.postalCode,
        };

        if (dialogMode === DIALOG_MODE.CREATE) {
          const newClient = await clientService.create(payload);
          toast.success("Cliente criado com sucesso!");

          setClients((prev) => [newClient, ...prev]);
          setTotalElements((prev) => prev + 1);
        } else if (dialogMode === DIALOG_MODE.EDIT && selectedClient) {
          const updatedClient = await clientService.update(
            selectedClient.id,
            payload,
          );
          toast.success("Cliente atualizado com sucesso!");

          setClients((prevClients) =>
            prevClients.map((c) =>
              c.id === updatedClient.id ? updatedClient : c,
            ),
          );
        }
        handleCloseDialog();
      } catch (error: unknown) {
        if (!applyFieldErrorsToForm(error, form)) {
          toast.error(getApiErrorMessage(error, "Erro ao salvar cliente"));
        }
      } finally {
        setLoading(false);
      }
    },
    [dialogMode, selectedClient, handleCloseDialog, form],
  );

  const handleDelete = useCallback(async () => {
    if (!selectedClient) return;

    setLoading(true);
    try {
      await clientService.delete(selectedClient.id);
      toast.success("Cliente excluído com sucesso!");

      const updatedClients = clients.filter((c) => c.id !== selectedClient.id);
      setClients(updatedClients);

      const newTotalElements = totalElements - 1;
      setTotalElements(newTotalElements);

      const newTotalPages = Math.ceil(newTotalElements / pageSize);
      setTotalPages(newTotalPages);

      if (updatedClients.length === 0 && currentPage > 0) {
        await loadClients(currentPage - 1);
      }

      handleCloseDialog();
    } catch (error: unknown) {
      toast.error(getApiErrorMessage(error, "Erro ao excluir cliente"));
    } finally {
      setLoading(false);
    }
  }, [
    selectedClient,
    clients,
    totalElements,
    pageSize,
    currentPage,
    handleCloseDialog,
    loadClients,
  ]);

  const handlePageChange = useCallback(
    (newPage: number) => {
      if (newPage >= 0 && newPage < totalPages) {
        loadClients(newPage);
      }
    },
    [totalPages, loadClients],
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Clientes</h1>
          <p className="text-muted-foreground">
            Gerencie o cadastro de clientes
          </p>
        </div>
        <Can permission={ACTIONS.clients.create}>
          <Button
            className="gap-2"
            onClick={() => handleOpenDialog(DIALOG_MODE.CREATE)}
            disabled={loading}
          >
            <Plus className="w-4 h-4" />
            Novo Cliente
          </Button>
        </Can>
      </div>

      {isAdmin && (
        <Card>
          <CardHeader>
            <CardTitle>Filtros de Busca (Admin)</CardTitle>
            <CardDescription>
              Use os filtros abaixo para buscar clientes específicos
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Nome</label>
                <Input
                  placeholder="Buscar por nome..."
                  value={filterName}
                  onChange={(e) => setFilterName(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Documento</label>
                <Input
                  placeholder="Buscar por CPF/CNPJ..."
                  value={filterDocument}
                  onChange={(e) => setFilterDocument(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Telefone</label>
                <Input
                  placeholder="Buscar por telefone..."
                  value={filterPhone}
                  onChange={(e) => setFilterPhone(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Email</label>
                <Input
                  placeholder="Buscar por email..."
                  value={filterEmail}
                  onChange={(e) => setFilterEmail(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Gênero</label>
                <select
                  value={filterGender}
                  onChange={(e) => setFilterGender(e.target.value)}
                  className="h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <option value="">Todos</option>
                  <option value="M">Masculino</option>
                  <option value="F">Feminino</option>
                </select>
              </div>
            </div>
            <div className="mt-4 flex gap-2">
              <Button onClick={handleSearch} disabled={loading}>
                <Search className="w-4 h-4 mr-2" />
                Buscar
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setFilterName("");
                  setFilterDocument("");
                  setFilterGender("");
                  setFilterPhone("");
                  setFilterEmail("");
                  // Recarregar com filtros limpos na próxima busca manual
                }}
              >
                Limpar Filtros
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <CardTitle>Lista de Clientes</CardTitle>
              <CardDescription>
                {loading
                  ? "Carregando..."
                  : `Mostrando ${filteredClients.length} de ${totalElements} clientes`}
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Input
                placeholder="Filtrar na tabela..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="md:max-w-xs"
              />
              <Button
                variant="outline"
                size="icon"
                onClick={() => loadClients(currentPage)}
                disabled={loading}
                title="Atualizar lista"
              >
                <RefreshCw
                  className={`w-4 h-4 ${loading ? "animate-spin" : ""}`}
                />
              </Button>
              <select
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                }}
                className="h-10 w-full cursor-pointer rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                disabled={loading}
              >
                <option value={10}>10 / página</option>
                <option value={25}>25 / página</option>
                <option value={50}>50 / página</option>
                <option value={100}>100 / página</option>
              </select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading && clients.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              Carregando clientes...
            </div>
          ) : filteredClients.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              Nenhum cliente encontrado
            </div>
          ) : (
            <ClientsTable
              clients={filteredClients}
              loading={loading}
              currentPage={currentPage}
              totalPages={totalPages}
              onView={(client) => handleOpenDialog(DIALOG_MODE.VIEW, client)}
              onEdit={(client) => handleOpenDialog(DIALOG_MODE.EDIT, client)}
              onDelete={(client) =>
                handleOpenDialog(DIALOG_MODE.DELETE, client)
              }
              onPageChange={handlePageChange}
            />
          )}
        </CardContent>
      </Card>

      <ClientFormDialog
        open={
          dialogMode === DIALOG_MODE.CREATE || dialogMode === DIALOG_MODE.EDIT
        }
        mode={dialogContent === DIALOG_MODE.CREATE ? "create" : "edit"}
        loading={loading}
        form={form}
        onClose={handleCloseDialog}
        onSubmit={onSubmit}
      />

      <ClientDeleteDialog
        open={dialogMode === DIALOG_MODE.DELETE}
        loading={loading}
        client={selectedClient}
        onClose={handleCloseDialog}
        onConfirm={handleDelete}
      />

      {dialogContent === DIALOG_MODE.VIEW && (
        <ClientDetailsModal
          open={dialogMode === DIALOG_MODE.VIEW}
          client={selectedClient}
          onClose={handleCloseDialog}
        />
      )}
    </div>
  );
}
